import React, { useEffect, useRef, useState } from "react";
import { formatNumber, formatNumberCommas } from "@/shared/utils/formatters.ts";
import { mediaPhone } from "@/shared/theme/breakpoints";
import { pressedFade } from "@/shared/theme/pressed";
import styled from "styled-components";
import { AnalysisVideo } from "@/api/campaign.ts";
import { Icon } from "@/shared/components/Icon/Icon.tsx";

const Overlay = styled.div`
	position: absolute;
	left: 12px;
	right: 12px;
	bottom: 12px;
	z-index: 2;
	padding: 8px;
	background: rgba(255, 255, 255, 0.12);
	border-radius: 8px;
	box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
	backdrop-filter: blur(6.5px);
	-webkit-backdrop-filter: blur(6.5px);
	border: 1px solid rgba(255, 255, 255, 0.2);
	opacity: 0;
	transition: opacity 0.2s;

	/* Phones have no hover; the same stats sit under the thumbnail instead (PhoneDetails). */
	${mediaPhone`
		display: none;
	`}
`;

const Card = styled.div`
	position: relative;
	border-radius: 8px;
	overflow: hidden;
	cursor: pointer;
	transition: transform 0.2s;
	background: #000;

	&:hover {
		transform: translateY(-4px);

		${Overlay} {
			opacity: 1;
		}
	}

	${mediaPhone`
		overflow: visible;
		background: transparent;

		&:hover {
			transform: none;
		}

		${pressedFade}
	`}
`;

const MediaWrapper = styled.div`
	position: relative;
	width: 100%;
	padding-bottom: 177.78%; /* 9:16 aspect ratio */
	background: #1a1a1a;
	overflow: hidden;

	> img,
	> video {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	> video {
		opacity: 1;
		z-index: 1;
	}

	> img {
		z-index: 0;
	}

	${mediaPhone`
		border-radius: 8px;
	`}
`;

const PhoneDetails = styled.div`
	display: none;

	${mediaPhone`
		display: flex;
		flex-direction: column;
		gap: 6px;
		padding: 8px 4px 4px;
		font-family: "Poppins", sans-serif;
		color: #303030;
	`}
`;

const PhoneHandle = styled.div`
	overflow: hidden;
	font-size: 13px;
	font-weight: 500;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const PhoneStats = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 4px 8px;
	font-size: 12px;
	color: #454545;
`;

const PhoneStat = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	min-width: 0;
	white-space: nowrap;
`;

const Placeholder = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background: #111;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 12px;
	color: #666;
	padding: 24px;
	text-align: center;
	z-index: 0;
`;

const PlaceholderText = styled.div`
	font-size: 12px;
	font-weight: 500;
	line-height: normal;
	font-family: "Poppins", sans-serif;
`;

const StatRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	color: white;
	font-size: 12px;
	font-weight: 500;
	padding: 4px;

	> img {
		height: 16px;
		width: 16px;
		object-fit: contain;
	}

	> span {
		flex-shrink: 0;
	}

	> label {
		margin-left: auto;
		color: rgba(255, 255, 255, 0.7);
		font-size: 10px;
	}
`;

const CreatorHandle = styled.div`
	color: white;
	font-size: 13px;
	font-weight: 700;
	margin-bottom: 4px;
	padding: 0 4px;
`;

interface VideoCardProps {
	video: AnalysisVideo;
	onClick?: (videoId: string) => void;
	campaignId?: string;
}

export const VideoCard: React.FC<VideoCardProps> = ({ video, onClick }) => {
	const videoRef = useRef<HTMLVideoElement>(null);
	const thumbnailCandidates = Object.values(video.thumbnails || {}).filter((thumbnail): thumbnail is string =>
		Boolean(thumbnail),
	);
	const [thumbnailIndex, setThumbnailIndex] = useState(0);
	const [imgSrc, setImgSrc] = useState<string>(thumbnailCandidates[0] || "");

	useEffect(() => {
		setThumbnailIndex(0);
		setImgSrc(thumbnailCandidates[0] || "");
	}, [video.id, JSON.stringify(video.thumbnails || {})]);

	const totalEngagement =
		(Number(video.likes) || 0) +
		(Number(video.comments) || 0) +
		(Number(video.shares) || 0) +
		(Number(video.saves) || 0);

	const handleMouseEnter = () => {
		videoRef.current?.play().catch(() => {}); // Play preview on hover
	};

	const handleMouseLeave = () => {
		if (videoRef.current) {
			videoRef.current.pause();
			videoRef.current.currentTime = 0;
		}
	};

	const handleClick = () => {
		onClick?.(video.id);
		if (video.url) {
			window.open(video.url, "_blank", "noopener,noreferrer");
		}
	};

	return (
		<Card onClick={handleClick} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
			<MediaWrapper>
				{/* The Poster/Thumbnail */}
				{imgSrc ? (
					<img
						src={imgSrc}
						alt="Thumbnail"
						referrerPolicy="no-referrer"
						onError={() => {
							const nextIndex = thumbnailIndex + 1;
							if (nextIndex < thumbnailCandidates.length) {
								setThumbnailIndex(nextIndex);
								setImgSrc(thumbnailCandidates[nextIndex]);
								return;
							}
							setImgSrc("");
						}}
					/>
				) : (
					<Placeholder>
						<Icon src="/icon/add-photo.svg" alt="Updating" size={40} />
						<PlaceholderText>Thumbnail is being updated</PlaceholderText>
					</Placeholder>
				)}

				<Overlay>
					<CreatorHandle>@{video.author?.handleName || "creator"}</CreatorHandle>

					<StatRow>
						<Icon src="/icon/play-small.svg" alt="Views" size={16} />
						<span>{formatNumberCommas(Number(video.viewCount) || 0)}</span>
						<label>Views</label>
					</StatRow>

					<StatRow>
						<Icon name="handHeart" alt="Engagement" size={16} preserveOriginalColor />
						<span>{video.engagementRate ?? "0.0%"}</span>
						<label>Eng. Rate</label>
					</StatRow>

					<StatRow>
						<Icon src="/icon/thumbs-up.svg" alt="Total Engagements" size={16} />
						<span>{formatNumberCommas(totalEngagement)}</span>
						<label>Total Engagements</label>
					</StatRow>
				</Overlay>
			</MediaWrapper>
			<PhoneDetails>
				<PhoneHandle>@{video.author?.handleName || "creator"}</PhoneHandle>
				<PhoneStats>
					<PhoneStat aria-label={`Views ${formatNumber(Number(video.viewCount) || 0)}`}>
						<Icon src="/icon/play-small.svg" alt="" size={16} color="#454545" />
						{formatNumber(Number(video.viewCount) || 0)}
					</PhoneStat>
					<PhoneStat aria-label={`Engagement rate ${video.engagementRate ?? "0.0%"}`}>
						<Icon name="handHeart" alt="" size={16} color="#454545" />
						{video.engagementRate ?? "0.0%"}
					</PhoneStat>
					<PhoneStat aria-label={`Total engagements ${formatNumber(totalEngagement)}`}>
						<Icon src="/icon/thumbs-up.svg" alt="" size={16} color="#454545" />
						{formatNumber(totalEngagement)}
					</PhoneStat>
				</PhoneStats>
			</PhoneDetails>
		</Card>
	);
};
